/**
 * When the day-of ticket card is on screen.
 *
 * A pure rule in its own module because `actions/live-event-card.ts` is a
 * `"use server"` file — every export there has to be an async server action, so
 * a plain predicate cannot live beside its caller, and a rule about clocks and
 * calendars is exactly the thing that needs a test rather than a live event to
 * check it against.
 */

/**
 * Lead-in the evening before. Twelve hours, so a morning event is on screen
 * when somebody checks the app the night before.
 */
export const LIVE_CARD_LEAD_MS = 12 * 60 * 60 * 1000;

/**
 * How far ahead the SQL reaches. Wider than the rule needs, because the local
 * calendar day of an event can begin well before `now + lead` in UTC terms; the
 * precise decision is made here, on a handful of the viewer's own rows.
 */
export const LIVE_CARD_QUERY_LOOKAHEAD_MS = 36 * 60 * 60 * 1000;

/** Keep the post-event review nudge available for one week. */
export const POST_EVENT_REVIEW_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

export function localDayKey(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA-u-ca-gregory-nu-latn", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

/**
 * Is this occurrence inside the card's window?
 *
 * Open for the whole of the event's local calendar day, OR from twelve hours
 * before it starts — whichever comes first — and closed the moment it ends.
 *
 * Two rules rather than one because either alone gets a real case wrong. A pure
 * "N hours before" rule with a small N surfaces a 9am class at 3am; with a large
 * N it surfaces a 9pm gig the previous morning. A pure calendar-day rule leaves
 * somebody who checks the app at 11pm the night before a 10am event with
 * nothing.
 */
export function isWithinLiveCardWindow(input: {
  now: Date;
  startTime: Date;
  endTime: Date;
  timeZone: string;
}): boolean {
  const { now, startTime, endTime, timeZone } = input;
  if (now.getTime() >= endTime.getTime()) return false;

  // Same local day, or already past it — an overnight event still counts on the
  // morning after, right up until it ends.
  if (localDayKey(startTime, timeZone) <= localDayKey(now, timeZone)) {
    return true;
  }

  return now.getTime() >= startTime.getTime() - LIVE_CARD_LEAD_MS;
}

export function isWithinPostEventReviewWindow(input: {
  now: Date;
  endTime: Date;
}): boolean {
  const elapsed = input.now.getTime() - input.endTime.getTime();
  return elapsed > 0 && elapsed <= POST_EVENT_REVIEW_WINDOW_MS;
}
